<nav class="sidebar" [class.sidebar-collapsed]="isCollapsed()">
  <!-- ONE shared description for temporally-dimmed items (the aria-describedby
       target must RESOLVE to text; a dangling id announces nothing). -->
  <span class="sr-only" id="temporal-nav-note">{{ 'temporal.nav.todayOnly' | translate }}</span>
  <div class="sidebar-content">
    <!-- Collapse Toggle -->
    <button class="sidebar-toggle" (click)="toggleSidebar()">
      <fa-duotone-icon [icon]="isCollapsed() ? faChevronRight : faChevronLeft"></fa-duotone-icon>
    </button>

    <!-- Scrollable nav area -->
    <div class="sidebar-nav-area">
      <!-- Sliding active indicator -->
      @if (indicatorTop() !== null) {
        <div class="nav-indicator" [style.top.px]="indicatorTop()"></div>
      }

      <!-- Main Navigation -->
      <div class="sidebar-section">
        <ul class="sidebar-nav">
          @for (item of visibleMainNavItems(); track item.route) {
            @if (item.separatorBefore) {
              <li class="sidebar-separator"></li>
            }
            <li class="sidebar-item">
              <a class="sidebar-link"
                 [routerLink]="linkFor(item)"
                 [appTourTarget]="tourKeyForRoute(item.route)"
                 [class.active]="isActive(linkFor(item))"
                 [class.temporal-dimmed]="isTemporalExcluded(item.route)"
                 [attr.aria-describedby]="isTemporalExcluded(item.route) ? 'temporal-nav-note' : null"
                 [pTooltip]="isCollapsed() ? (item.labelKey ? (item.labelKey | translate) : item.label) : ''"
                 tooltipPosition="right"
                 [showDelay]="300">
                <span class="sidebar-icon">
                  <fa-duotone-icon [icon]="item.icon"></fa-duotone-icon>
                </span>
                @if (!isCollapsed()) {
                  <span class="sidebar-label">{{ item.labelKey ? (item.labelKey | translate) : item.label }}</span>
                  @if (item.badge && item.badge > 0) {
                    <span class="count-badge count-badge--primary">{{ formatCount(item.badge) }}</span>
                  }
                }
              </a>
            </li>
          }
        </ul>
      </div>

      <!-- Admin Navigation — visible when user is Admin/Owner OR holds any admin nav permission -->
      @if (showAdminSection()) {
        <div class="sidebar-section" [appTourTarget]="'management-section'">
          @if (!isCollapsed()) {
            <h4 class="sidebar-section-title">{{ 'navigation.admin.title' | translate | uppercase }}</h4>
          }
          <ul class="sidebar-nav">
            @for (item of adminNavItems; track item.route) {
              @if (isNavItemVisible(item)) {
                <li class="sidebar-item">
                  <a class="sidebar-link"
                     [routerLink]="item.route"
                     [class.active]="isActive(item.route)"
                     [class.temporal-dimmed]="isTemporalExcluded(item.route)"
                     [attr.aria-describedby]="isTemporalExcluded(item.route) ? 'temporal-nav-note' : null"
                     [pTooltip]="isCollapsed() ? (item.labelKey ? (item.labelKey | translate) : item.label) : ''"
                     tooltipPosition="right"
                     [showDelay]="300">
                    <span class="sidebar-icon">
                      <fa-duotone-icon [icon]="item.icon"></fa-duotone-icon>
                    </span>
                    @if (!isCollapsed()) {
                      <span class="sidebar-label">{{ item.labelKey ? (item.labelKey | translate) : item.label }}</span>
                      @if (item.badge && item.badge > 0) {
                        <span class="count-badge count-badge--primary">{{ formatCount(item.badge) }}</span>
                      }
                    }
                  </a>
                </li>
              }
            }
          </ul>
        </div>
      }
    </div>

    <!-- Branduals Logo -->
    <div class="sidebar-footer">
      <a href="https://branduals.com" target="_blank" class="branduals-logo" 
         [pTooltip]="isCollapsed() ? 'Branduals' : ''"
         tooltipPosition="right"
         [showDelay]="300">
        @if (!isCollapsed()) {
          <!-- Full Branduals logo -->
          <svg viewBox="0 0 154 34">
            <path d="M0,26.3V0.4h4.1v9c1-0.7,2.4-1.2,3.9-1.2c4.1,0,7,2.9,7,7v11.1c0,4.1-3.1,7.3-7.5,7.3S0,30.4,0,26.3z M10.9,26.3V15.6c0-1.9-1.5-3.4-3.4-3.4s-3.4,1.5-3.4,3.4v10.7c0,1.9,1.5,3.4,3.4,3.4C9.4,29.4,10.9,28.2,10.9,26.3z" />
            <path d="M19.1,15.6c0-4.1,3.1-7.3,7.5-7.3H29v4.1h-2.4c-1.9,0-3.4,1.5-3.4,3.4v17.7h-4.1V15.6z" />
            <path d="M31,26.3c0-4.1,3.1-7,7-7c1.2,0,2.7,0.2,3.9,1.2v-4.8c0-1.9-1.5-3.4-3.4-3.4c-1.9,0-3.4,1.5-3.4,3.4v1.5H31 v-1.5c0-4.1,3.1-7.3,7.5-7.3s7.5,3.1,7.5,7.3v10.7c0,4.1-3.1,7.3-7.5,7.3S31,30.4,31,26.3z M41.9,26.3c0-1.9-1.5-3.4-3.4-3.4 s-3.4,1.5-3.4,3.4s1.5,3.4,3.4,3.4C40.4,29.4,41.9,28.2,41.9,26.3z" />
            <path d="M50.4,15.6c0-4.1,3.1-7.3,7.5-7.3s7.5,3.1,7.5,7.3v17.7h-4.1V15.6c0-1.9-1.5-3.4-3.4-3.4 c-1.9,0-3.4,1.5-3.4,3.4v17.7h-4.1C50.4,33.1,50.4,15.6,50.4,15.6z" />
            <path d="M69.5,26.3V15.2c0-4.1,2.9-7,7-7c1.5,0,2.9,0.5,3.9,1.2v-9h4.1v25.9c0,4.1-3.1,7.3-7.5,7.3S69.5,30.4,69.5,26.3 z M80.1,26.3V15.6c0-1.9-1.5-3.4-3.4-3.4c-1.9,0-3.4,1.5-3.4,3.4v10.7c0,1.9,1.5,3.4,3.4,3.4C78.9,29.4,80.1,28.2,80.1,26.3z" />
            <path d="M88.6,26.3V8.6h4.1v17.7c0,1.9,1.5,3.4,3.4,3.4c1.9,0,3.4-1.5,3.4-3.4V8.6h4.1v17.7c0,4.1-3.1,7.3-7.5,7.3 C91.7,33.6,88.6,30.4,88.6,26.3z" />
            <path d="M107.7,26.3c0-4.1,3.1-7,7-7c1.2,0,2.7,0.2,3.9,1.2v-4.8c0-1.9-1.5-3.4-3.4-3.4c-1.9,0-3.4,1.5-3.4,3.4v1.5 h-4.1v-1.5c0-4.1,3.1-7.3,7.5-7.3c4.4,0,7.5,3.1,7.5,7.3v10.7c0,4.1-3.1,7.3-7.5,7.3C110.9,33.6,107.7,30.4,107.7,26.3z M118.6,26.3 c0-1.9-1.5-3.4-3.4-3.4c-1.9,0-3.4,1.5-3.4,3.4s1.5,3.4,3.4,3.4C117.2,29.4,118.6,28.2,118.6,26.3z" />
            <path d="M127.1,0.4h4.1v32.7h-4.1V0.4z" />
            <path d="M139.7,24.8v1.5c0,1.9,1.5,3.4,3.4,3.4s3.4-1.5,3.4-3.4c0-1-0.2-1.9-1-2.4c-1.9-1.7-5.1-0.5-7.7-3.1 c-1.5-1.2-2.2-3.1-2.2-5.1c0-4.1,3.1-7.3,7.5-7.3c4.4,0,7.5,3.1,7.5,7.3v1.2h-4.1v-1.2c0-1.9-1.5-3.4-3.4-3.4s-3.4,1.5-3.4,3.4 c0,1,0.2,1.9,1,2.4c1.7,1.5,5.1,0.5,7.7,3.1c1.5,1.2,2.2,3.1,2.2,5.3c0,4.1-3.1,7.3-7.5,7.3c-4.4,0-7.5-3.1-7.5-7.3v-1.5 C135.6,24.8,139.7,24.8,139.7,24.8z" />
          </svg>
        } @else {
          <!-- Collapsed logo - just "br" -->
          <svg viewBox="0 0 34 34">
            <path d="M0,26.3V0.4h4.1v9c1-0.7,2.4-1.2,3.9-1.2c4.1,0,7,2.9,7,7v11.1c0,4.1-3.1,7.3-7.5,7.3S0,30.4,0,26.3z M10.9,26.3V15.6c0-1.9-1.5-3.4-3.4-3.4s-3.4,1.5-3.4,3.4v10.7c0,1.9,1.5,3.4,3.4,3.4C9.4,29.4,10.9,28.2,10.9,26.3z" />
            <path d="M19.1,15.6c0-4.1,3.1-7.3,7.5-7.3H29v4.1h-2.4c-1.9,0-3.4,1.5-3.4,3.4v17.7h-4.1V15.6z" />
          </svg>
        }
      </a>
    </div>
  </div>
</nav>
